import { catalogueNameKey, TRUCK_YEAR_FLOOR, maxTruckYear, type TruckMakeView } from '@chukua/common';

export interface PickerOption {
  value: string;
  label: string;
}

/** A truck saved before the catalogue can hold a value the catalogue lacks; it stays selectable so opening the form never blanks it. */
function withCurrent(options: PickerOption[], current: string | undefined): PickerOption[] {
  if (!current) return options;
  const key = catalogueNameKey(current);
  return options.some((option) => catalogueNameKey(option.value) === key) ? options : [{ value: current, label: current }, ...options];
}

export function makeOptions(makes: TruckMakeView[], current: string | undefined): PickerOption[] {
  return withCurrent(
    makes.map((make) => ({ value: make.name, label: make.name })),
    current,
  );
}

export function findMake(makes: TruckMakeView[], name: string | undefined): TruckMakeView | undefined {
  if (!name) return undefined;
  const key = catalogueNameKey(name);
  return makes.find((make) => catalogueNameKey(make.name) === key);
}

/** The models of the chosen make only; none until a make is chosen. A make outside the catalogue has no models to list beyond the truck's own. */
export function modelOptions(makes: TruckMakeView[], makeName: string | undefined, current: string | undefined): PickerOption[] {
  const models = findMake(makes, makeName)?.models ?? [];
  return withCurrent(
    models.map((model) => ({ value: model.name, label: model.name })),
    current,
  );
}

/** Newest first, from next year down to the floor. */
export function yearOptions(current: number | undefined, now: Date = new Date()): { value: number; label: string }[] {
  const years: number[] = [];
  for (let year = maxTruckYear(now); year >= TRUCK_YEAR_FLOOR; year -= 1) years.push(year);
  if (current !== undefined && !years.includes(current)) years.unshift(current);
  return years.map((year) => ({ value: year, label: String(year) }));
}

/** Offer to add what was typed only when it is something and nothing listed already has that name. */
export function canOfferAdd(typed: string, options: PickerOption[]): boolean {
  const key = catalogueNameKey(typed);
  return key !== '' && !options.some((option) => catalogueNameKey(option.value) === key);
}
